import { useEffect, useRef } from "react";
import { useQuery } from "@powersync/react";
import { detectPlatform } from "@ect/core";
import { navigate } from "./router";
import { useSession } from "./session";
import { enablePush, refreshPushIfGranted } from "./push";

interface Row { id: string; type: string; title: string; body: string | null; payload: string | null; created_at: string }

/** Where a tapped notification leads (admin shell routes; the field shell simply comes to the foreground). */
export function openNotificationTarget(data: Record<string, string>, isAdmin: boolean) {
  if (!isAdmin) return;
  if (data.need_id || data.type?.startsWith("need") || data.type === "purchase_list") navigate("purchasing");
  else if (data.chantier_id) navigate(`chantiers/${data.chantier_id}`);
  else navigate("");
}

/**
 * Mounted once inside SyncProvider: (1) re-registers push silently when already allowed, (2) on ECT Desk shows a Windows
 * toast for every notification row that arrives through sync after start-up (no push service needed on desktop),
 * (3) handles notification taps coming from the service worker.
 */
export function NotificationsBootstrap() {
  const { session, api } = useSession();
  const isAdmin = !!session?.user.roles.some((r) => r.role === "group_admin" || r.role === "entity_admin");
  const platform = detectPlatform();
  const since = useRef(new Date().toISOString());
  const seen = useRef(new Set<string>());
  const { data } = useQuery<Row>("select id, type, title, body, payload, created_at from notifications where user_id = ? and read_at is null and created_at > ? order by created_at desc limit 20", [session?.user.id ?? "", since.current]);

  // Native app: ask once at first login (the Android permission dialog is expected there); browsers ask only from the settings page.
  useEffect(() => {
    if (!session) return;
    void (async () => {
      const onOpen = (d: Record<string, string>) => openNotificationTarget(d, isAdmin);
      if (platform === "capacitor") {
        const key = `ect.pushAsked.${session.user.id}`;
        let asked = false;
        try { asked = localStorage.getItem(key) === "1"; } catch { /* ignore */ }
        if (!asked) { try { localStorage.setItem(key, "1"); } catch { /* ignore */ } await enablePush(api, onOpen).catch((e) => console.warn("[push] enable failed", e)); return; }
      }
      await refreshPushIfGranted(api, onOpen);
    })();
  }, [session, api, isAdmin, platform]);

  useEffect(() => {
    if (platform !== "tauri" || !data.length) return;
    void (async () => {
      const { isPermissionGranted, requestPermission, sendNotification } = await import("@tauri-apps/plugin-notification");
      let ok = await isPermissionGranted();
      if (!ok) ok = (await requestPermission()) === "granted";
      if (!ok) return;
      for (const n of data) {
        if (seen.current.has(n.id)) continue;
        seen.current.add(n.id);
        sendNotification({ title: n.title, body: n.body ?? undefined });
      }
    })();
  }, [data, platform]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const on = (e: MessageEvent) => { if (e.data?.kind === "notification-open") openNotificationTarget(e.data.data ?? {}, isAdmin); };
    navigator.serviceWorker.addEventListener("message", on);
    const q = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
    if (q.get("notif")) openNotificationTarget({ type: q.get("type") ?? "", need_id: q.get("need") ?? "", chantier_id: q.get("chantier") ?? "" }, isAdmin);
    return () => navigator.serviceWorker.removeEventListener("message", on);
  }, [isAdmin]);
  return null;
}
